import { useParams } from "react-router-dom";
import { Grid, Tooltip, Typography } from "@mui/material";
import * as L from "leaflet";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useEmissions, usePermissions } from "src/hooks";
import { useScenario } from "src/hooks/useScenario";
import { Pane } from "src/components/Pane";
import { PaneListEmptyPlaceholder } from "src/components/PaneListEmptyPlaceholder";
import { EmissionListItem } from "src/components/EmissionListItem";
import { EmissionListItemIcon } from "src/components/EmissionListItemIcon";
import { EmissionType } from "src/types";
import { IsEnabled, IsVisible } from "src/components/permissions";

interface Props {
  map: L.Map | null; // the map is great to have, but creating a Poi without visual marker is possible
}

export function EmissionsPage(props: Props) {
  const { t } = useTranslation();
  const { caseId = "", scenarioId = "" } = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
  }>();
  const { data: emissions } = useEmissions(scenarioId);
  const { data: scenario } = useScenario(scenarioId);
  const { data: permissions } = usePermissions();

  return render();

  function render() {
    if (!emissions) return null;

    return (
      <Pane>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h2">{t("label.emissions")}</Typography>
          </Grid>

          {renderEmissionsList()}

          {/* Version 2.0` */}
          {/* <Grid item xs={12}>
            <Button
              startIcon={<AddCircleIcon />}
              color="primary"
              <>
              component={NavLink}
              to={`/cases/${acase.id}/pois/new`}
            >
              {t('label.addEmission')}
            </Button>
          </Grid> */}
        </Grid>
      </Pane>
    );
  }

  function renderEmissionsList() {
    if (!permissions) return null;
    if (!emissions) return null;
    if (!scenario) return null;

    if (emissions.length === 0) {
      return (
        <Grid item xs={12}>
          <PaneListEmptyPlaceholder>{t("label.noEmissionFound")}</PaneListEmptyPlaceholder>
        </Grid>
      );
    }

    return (
      <>
        {emissions.map((emission) => (
          <Grid item xs={12} key={[emission.id, emission.type].join()}>
            <EmissionListItem caseId={caseId} scenario={scenario} emission={emission} />
          </Grid>
        ))}
        <IsEnabled permission={permissions?.scenario.residential}>
          <IsVisible permission={permissions?.scenario.residential}>
            <Grid item xs={12}>
              <ResidentialEmissionListItem />
            </Grid>
          </IsVisible>
        </IsEnabled>
      </>
    );
  }
}

/**
 * This is a seperate component, because it has different behaviour then the other emission types
 * Only a tooltip; with information is visible. Interaction is disabled.
 *
 * No seperate module or export. This is not meant to be used elsewhere.
 * When RESIDENTIAL emssions become "useful" in this page, it can shift to the regular EmissionListItem component
 */
function ResidentialEmissionListItem() {
  const { t } = useTranslation();

  return render();

  function render() {
    return (
      <Tooltip
        title={
          <>
            <Typography variant="body1" color="inherit">
              {t("label.residentialEmissionTooltip")}
            </Typography>
          </>
        }
        placement="right"
        followCursor
      >
        <ResidentialEmissionListItemStyles>
          <EmissionListItemIcon type={EmissionType.RESIDENTIAL} />
          <div className="name">{t(`label.emissionType.${EmissionType.RESIDENTIAL}`)}</div>
        </ResidentialEmissionListItemStyles>
      </Tooltip>
    );
  }
}

const ResidentialEmissionListItemStyles = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 75px;
  width: 100%;
  padding: 0 17px;

  border-radius: 4px;
  background-color: #f1f1f1;
  color: #000000;
  font-weight: 500;

  cursor: not-allowed;

  > .MuiSvgIcon-root {
    fill: #000000;
    width: 25px;
  }
`;
